import React, { Suspense, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Home from "./pages/Home";
import Solutions from "./pages/Solutions";
import About from "./pages/About";
import Projects from "./pages/Projects";
import Contact from "./pages/Contact";
import SmoothScrollLink from "./components/SmoothScrollLink";
import ThreeViewer from "./components/ThreeViewer";

const MobileSections = () => {
  const { t } = useTranslation();
  const bgImage = process.env.PUBLIC_URL + "/assets/bg.jpg";

  // 3D Models setup (restoring original order)
  const models = [
    process.env.PUBLIC_URL + "/assets/infra_data.glb",
    process.env.PUBLIC_URL + "/assets/sample.glb",
    process.env.PUBLIC_URL + "/assets/facade.glb",
  ];

  const [showGlow, setShowGlow] = useState(false);
  const [breathingComplete, setBreathingComplete] = useState([
    false,
    false,
    false,
  ]);
  const [interactingModels, setInteractingModels] = useState([
    false,
    false,
    false,
  ]);
  const viewerRefs = useRef([]);

  const handleInteraction = (index) => {
    setInteractingModels((prev) => {
      const newState = [...prev];
      newState[index] = true;
      return newState;
    });

    // Reset after a short delay
    setTimeout(() => {
      setInteractingModels((prev) => {
        const newState = [...prev];
        newState[index] = false;
        return newState;
      });
    }, 300); // Short delay to show the interaction state
  };

  useEffect(() => {
    const viewers = viewerRefs.current;
    const timer = setTimeout(() => {
      setShowGlow(true);
      viewers.forEach((viewer, index) => {
        if (viewer) {
          viewer.classList.add(`glow-effect-${index + 1}`);
          setTimeout(() => {
            setBreathingComplete((prev) => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
          }, 4000); // 4 seconds for the breathing animation
        }
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="mobile-container">
      {/* Unified Mobile Hero Section with 3D Models */}
      <section id="mobile-hero" className="mobile-hero-section">
        <div
          className="hero-content"
          style={{
            backgroundImage: `url(${bgImage})`,
            minHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="relative z-10 px-4">
            {/* Hero Text */}
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-text">{t("home.hero.digital")}</span>{" "}
                <span className="blinking-text-1">{t("home.hero.planning")}</span>{" "}
                <span className="gradient-text">{t("home.hero.sustainable")}</span>{" "}
                <span className="blinking-text-2">{t("home.hero.construction")}</span>
              </h1>
              <p className="text-lg text-white opacity-90 max-w-2xl mx-auto mb-6">
                {t("home.description")}
              </p>
              <button className="btn-primary mx-2">
                <SmoothScrollLink to="/contact" sectionId="contact">
                  {t("home.getStarted")}
                </SmoothScrollLink>
              </button>
            </div>

            {/* 3D Models Section */}
            <div className="mobile-3d-section mt-8">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                  zIndex: 1,
                  width: "100%",
                  maxWidth: "1400px",
                  margin: "0 auto",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                {models.map((modelPath, index) => (
                  <div
                    key={index}
                    ref={(el) => (viewerRefs.current[index] = el)}
                    className={`three-viewer-container ${showGlow ? `glow-effect-${index + 1}` : ""
                      } ${breathingComplete[index] ? "reduced-glow" : ""} ${interactingModels[index] ? "grabbed" : ""
                      }`}
                    style={{
                      width: index === 1 ? "300px" : "250px",
                      height: index === 1 ? "300px" : "250px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      backgroundColor: "transparent",
                    }}
                  >
                    <Suspense
                      fallback={
                        <div className="loading-text">{t("home.loading")}</div>
                      }
                    >
                      <ThreeViewer
                        modelPath={modelPath}
                        index={index}
                        isInteracting={interactingModels[index]}
                        onInteraction={() => handleInteraction(index)}
                      />
                    </Suspense>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider bg-gradient-to-r from-transparent via-orange-500 to-transparent h-px opacity-50"></div>

      {/* Solutions Section */}
      <section id="solutions" className="mobile-section">
        <div className="section-header">
          <h2 className="text-2xl font-bold text-center mb-4 gradient-text">
            {t("navigation.solutions")}
          </h2>
        </div>
        <Solutions hideHero={true} />
      </section>

      {/* Section Divider */}
      <div className="section-divider bg-gradient-to-r from-transparent via-orange-500 to-transparent h-px opacity-50 my-8"></div>

      {/* About Section */}
      <section id="about" className="mobile-section">
        <div className="section-header">
          <h2 className="text-2xl font-bold text-center mb-4 gradient-text">
            {t("navigation.about")}
          </h2>
        </div>
        <About hideHero={true} />
      </section>

      {/* Section Divider */}
      <div className="section-divider bg-gradient-to-r from-transparent via-orange-500 to-transparent h-px opacity-50 my-8"></div>

      {/* Projects Section */}
      <section id="projects" className="mobile-section">
        <div className="section-header">
          <h2 className="text-2xl font-bold text-center mb-4 gradient-text">
            {t("navigation.projects")}
          </h2>
        </div>
        <Suspense fallback={<div className="text-center py-8">{t("home.loading")}</div>}>
          <Projects hideHero={true} />
        </Suspense>
      </section>

      {/* Section Divider */}
      <div className="section-divider bg-gradient-to-r from-transparent via-orange-500 to-transparent h-px opacity-50 my-8"></div>

      {/* Contact Section */}
      <section id="contact" className="mobile-section">
        <div className="section-header">
          <h2 className="text-2xl font-bold text-center mb-4 gradient-text">
            {t("navigation.contact")}
          </h2>
        </div>
        <Contact hideHero={true} />
      </section>
    </div>
  );
};

export default MobileSections;
